<template>
    <div>
        <template v-if="visible">
            <!-- ######################## -->
            <!-- selection based          -->
            <!-- ######################## -->
            <template v-if="needSelection">
                <div v-if="longTitle" class="input-form-setting">
                    {{ title }}
                </div>
                <el-form-item :ref="parameter.jsonKey">
                    <div v-if="!longTitle" slot="label" class="input-form-setting">
                        {{ title }}
                    </div>
                    <el-select v-model="parameter.value" :no-data-text="noDataTextOfSelection" :disabled="disabled" @change="handleOptionChange">
                        <el-option v-for="(candidate, index) in parameter.candidates" :key="candidate.name.en" :label="getCandidateName(candidate)" :value="index"></el-option>
                    </el-select>
                </el-form-item>
            </template>

            <!-- ######################## -->
            <!-- storage folder selection -->
            <!-- ######################## -->
            <template v-else-if="findCommand('selectStorageFolder')">
                <el-form-item :ref="parameter.jsonKey">
                    <div slot="label" class="input-form-setting">
                        {{ title }}
                        <button class="transparent" type="button" v-on:click="openStorageFolderSelection()"><span class="normal-command-text" v-t="'message.title_selection_do'"></span></button>
                        <button class="transparent" type="button" v-on:click="deleteSelectedStorage()"><span class="normal-command-text" v-t="'message.title_selection_cancel'"></span></button>
                    </div>
                    <el-input type="text" :placeholder="placeHolderOfFolderSelection" v-model="folderName" disabled="true" @change="update"/>
                </el-form-item>

                <DefinitionParameterStorageFolderSelection
                    v-if="showStorageFolderSelection"
                    :keyVaultId="dependValue" :targetParameter="parameter"
                    @cancel="cancelStorageFolderSelection" @save="saveStorageFolderSelection"
                />

            </template>

            <!-- ######################## -->
            <!-- normal input             -->
            <!-- ######################## -->
            <template v-else>
                <div v-if="longTitle" class="input-form-setting">
                    {{ title }}
                </div>
                <el-form-item :ref="parameter.jsonKey">
                    <div v-if="!longTitle" slot="label" class="input-form-setting">
                        {{ title }}
                    </div>
                    <el-input
                        :type="type" :placeholder="placeHolder" v-model="parameter.value" :disabled="disabled"
                        @compositionstart.native="compositionStart" @compositionend.native="compositionEnd"
                        @input ="inputCheck"
                        clearable @clear="inputCheck">
                    </el-input>
                </el-form-item>
            </template>
        </template>
    </div>
</template>

<script>
import commonUtil from '@/components/commonutil.js';
import processorTemplateUtil from "@/components/pc/setting/ai-app/AiAppSettingUtil";
import DefinitionParameterStorageFolderSelection from "@/components/pc/setting/ai-app/DefinitionParameterStorageFolderSelection.vue";

export default {
    components: { DefinitionParameterStorageFolderSelection },

    props: {
        parameter: {
            type: Object,
            required: true,
        },
        dependParameter: {
            type: String,
            default: null
        },
        addCase: {
            type: Boolean,
            default: true
        },
    },

    data() {
        return {
            validator: null,
            dependValue: null,

            enableSave: null,
            showStorageFolderSelection: false,

            placeHolderOfFolderSelection: commonUtil.getMessageText(this.$i18n, 'info_empty_storage_selection'),
            folderNameLoading: false,
            folderName: null,
        };
    },

    watch: {
        'folderNameLoading': function() {
            this.placeHolderOfFolderSelection = this.folderNameLoading ? 'Loading...' : commonUtil.getMessageText(this.$i18n, 'info_empty_storage_selection');
        },
    },

    created: function() {
        this.validator = processorTemplateUtil.getParameterValidator(this.parameter);
        if (this.parameter.shownValue != null) {
            this.folderName = this.parameter.shownValue;
        }
    },

    mounted() {
        this.inputCheck();
        if (this.visible && (this.validator == null || this.validator.valid)) {
            this.update()
            this.loadFolderNameIfRequired();
        }
    },

    computed: {
        title() {
            return processorTemplateUtil.getLocaledText(this.$i18n, this.parameter.title);
        },
        longTitle() {
            let index = 0;
            let length = this.title.length;
            let weight = 0;

            while (index < length) {
                let codePoint = this.title.codePointAt(index);
                // TODO: take into account characters other than Japanese
                if (codePoint <= 0x7F) {
                    // half-width
                    weight++;
                } else if (0xff66 <= codePoint && codePoint <= 0xff9d) {
                    // half-width kana
                    weight++;
                } else {
                    weight += 2;
                }

                if (codePoint >= 0x010000) {
                    index += 2;
                } else {
                    index++;
                }
            }

            return (weight >= 32);
        },
        placeHolder() {
            return processorTemplateUtil.getPlaceHolder(this.$i18n, this.parameter);
        },
        noDataTextOfSelection() {
            if (this.$i18n.locale === 'ja') {
                return commonUtil.getMessageText(this.$i18n, "info_no_selection_data").replace("{selectionItemName}", this.parameter.title.ja);
            } else {
                return commonUtil.getMessageText(this.$i18n, "info_no_selection_data").replace("{selectionItemName}", this.parameter.title.en);
            }
        },
        type() {
            return processorTemplateUtil.getType(this.parameter);
        },
        visible() {
            return processorTemplateUtil.judgeVisible(this.parameter);
        },
        disabled() {
            return processorTemplateUtil.judgeDisabled(this.parameter);
        },
        needSelection() {
            return processorTemplateUtil.needSelection(this.parameter);
        },
        compositionStart() {
            this.parameter.isComposing = true;
        },
        compositionEnd() {
            this.parameter.isComposing = false;
            this.inputCheck();
        },
    },

    methods: {
        loadFolderNameIfRequired() {
            if (!this.findCommand('selectStorageFolder') || (this.folderName != null)) {
                // do nothing
                return;
            }
            this.getFolderName();
        },
        async getFolderName() {
            let keyVaultId = this.dependValue;
            this.folderNameLoading = true;
            this.folderName = await processorTemplateUtil.requestStorageFolderNameIfRequired(keyVaultId, this.parameter.value);
            this.folderNameLoading = false;
        },
        getCandidateName(candidate) {
            if (this.$i18n.locale === 'ja') {
                return candidate.name.ja;
            } else {
                return candidate.name.en;
            }
        },
        findCommand(command) {
            return processorTemplateUtil.findCommand(this.parameter, command);
        },
        handleOptionChange() {
            this.update()
            this.$emit('update:valid', true)
        },
        inputCheck() {
            if (this.validator == null) {
                this.$emit('update:valid', true)
                return;
            }

            let validateSkipped = processorTemplateUtil.validateItem(this.$i18n, this.validator, this.parameter.value);
            if (validateSkipped) {
                // inputting with IME
                return;
            }

            let ref = this.$refs[this.parameter.jsonKey];
            processorTemplateUtil.overwriteValidationResultToRef(ref, this.validator);
            this.$emit('update:valid', this.validator.valid)
            if (this.validator.valid) {
                this.update()
            }
        },
        update() {
            let value;
            if (this.needSelection) {
                value = this.parameter.candidates[this.parameter.value].value;
            } else {
                value = this.parameter.value;
            }
            this.$emit('update', this.parameter.jsonKey, value);
        },
        getDependParameter() {
            return this.dependParameter;
        },
        onChangeDependParameter(value) {
            // the first time at which depend value is set
            if (this.dependValue == null) {
                this.dependValue = value;
                return;
            }

            // same value
            if (this.dependValue == value) {
                return;
            }

            this.dependValue = value;
            this.parameter.value = '';
            this.folderName = '';
        },
        openStorageFolderSelection() {
            this.showStorageFolderSelection = true;
        },
        deleteSelectedStorage() {
            this.parameter.value = '';
            this.folderName = '';
            this.$emit('update:valid', true)
        },
        cancelStorageFolderSelection() {
            this.showStorageFolderSelection = false;
        },
        saveStorageFolderSelection(newFolderName) {
            this.folderName = newFolderName;
            this.showStorageFolderSelection = false;
            this.$emit('update:valid', true)
        },
    }
}
</script>

<style scoped>

.input-form-setting {
    padding-left: 15px;
    text-align: left;
}

.el-form-item {
    margin-right: 100px
}

</style>
